import { useEffect, useState } from 'react';
import { apiConfig } from '../authConfig';
import { useApiToken } from './useApiToken';

/**
 * The advisory firm behind a project: GET /api/v1/projects/:id/advisory-firm, readable by every
 * project member (Lead Advisor, Advisor, SPOC, Delegate) and platform admin, so each persona sees
 * the same real advisor-party values on the contract. Response { advisoryFirm: { firmName,
 * legalName, pan, gstin, address, city, state, country, pincode, logoUrl (SAS or null),
 * brandPrimary, brandFont } | null }.
 * status: 'idle' (no project) | 'loading' | 'ready' | 'none' (no firm) | 'error'. A missing or
 * failed firm is never replaced with invented data; callers show blanks and block the dependent
 * action.
 */
export function useAdvisoryFirmRecord(projectId) {
    const getToken = useApiToken();
    const [state, setState] = useState({ firm: null, status: 'idle' });

    useEffect(() => {
        if (!projectId) {
            setState({ firm: null, status: 'idle' });
            return undefined;
        }
        let cancelled = false;
        setState({ firm: null, status: 'loading' });
        (async () => {
            try {
                const token = await getToken();
                const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/advisory-firm`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const ct = res.headers?.get?.('content-type') || '';
                const json = ct.includes('json') ? await res.json().catch(() => null) : null;
                if (cancelled) return;
                if (res.ok && json?.success) {
                    const row = json.data?.advisoryFirm ?? null;
                    setState(row ? { firm: row, status: 'ready' } : { firm: null, status: 'none' });
                } else {
                    setState({ firm: null, status: 'error' });
                }
            } catch {
                if (!cancelled) setState({ firm: null, status: 'error' });
            }
        })();
        return () => { cancelled = true; };
    }, [projectId, getToken]);

    return state;
}
